import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, Loader2, Sparkles, Globe, MessageCircle } from 'lucide-react'
import api from '../api/client'

function HomePage() {
  const navigate = useNavigate()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return
    setUploading(true)
    setError(null)

    const formData = new FormData()
    formData.append('file', acceptedFiles[0])

    try {
      const res = await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      navigate(`/translate/${res.data.document_id}`)
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }, [navigate])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff', '.bmp'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    disabled: uploading,
  })

  const handleSampleUpload = async () => {
    setUploading(true)
    setError(null)
    try {
      const res = await api.post('/documents/upload-sample')
      navigate(`/translate/${res.data.document_id}`)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load sample document.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Hero */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-navy mb-4">
          Understand Your Clinical Trial
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Upload a clinical trial document and get a simple, clear explanation
          in your language. No medical degree needed.
        </p>
      </div>

      {/* Features */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <div className="card text-center">
          <Sparkles className="w-10 h-10 text-primary mx-auto mb-3" />
          <h3 className="font-semibold mb-1">Plain Language</h3>
          <p className="text-gray-500 text-base">Complex medical terms explained simply</p>
        </div>
        <div className="card text-center">
          <Globe className="w-10 h-10 text-primary mx-auto mb-3" />
          <h3 className="font-semibold mb-1">6 Languages</h3>
          <p className="text-gray-500 text-base">English, Spanish, Hindi, Portuguese & more</p>
        </div>
        <div className="card text-center">
          <MessageCircle className="w-10 h-10 text-primary mx-auto mb-3" />
          <h3 className="font-semibold mb-1">Ask Questions</h3>
          <p className="text-gray-500 text-base">Chat to understand any part better</p>
        </div>
      </div>

      {/* Upload Zone */}
      <div className="card">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors
            ${isDragActive ? 'border-primary bg-primary-light' : 'border-gray-300 hover:border-primary hover:bg-gray-50'}
            ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
              <p className="text-lg font-medium">Processing your document...</p>
              <p className="text-gray-500">Extracting text from your file</p>
            </div>
          ) : isDragActive ? (
            <div className="flex flex-col items-center gap-4">
              <Upload className="w-12 h-12 text-primary" />
              <p className="text-lg font-medium">Drop your file here</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <Upload className="w-12 h-12 text-gray-400" />
              <p className="text-lg font-medium">
                Drag & drop your clinical trial document
              </p>
              <p className="text-gray-500">
                PDF, images, or text files (max 10MB)
              </p>
              <button className="btn-primary mt-2">
                <span className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Choose File
                </span>
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            {error}
          </div>
        )}

        {/* Sample Document */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 mb-2">No document? Try our demo:</p>
          <button
            onClick={handleSampleUpload}
            disabled={uploading}
            className="btn-secondary"
          >
            <span className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Load Sample Trial Document
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default HomePage
